import { Check, Phone, Mail } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang } from "@/i18n";

const REPAIR_IMG = "https://images.unsplash.com/photo-1562408590-e32931084e23?q=80&w=1400&auto=format&fit=crop";

const SCOPE = [
    {
        de: "Grundig-Kopfstationskassetten & baugleiche Typen (Müller, Hirschmann, Arcom, G-Sat, Schwaiger)",
        en: "Grundig headend cassettes & identical types (Müller, Hirschmann, Arcom, G-Sat, Schwaiger)",
    },
    { de: "Bedienteile & komplette Kopfstationen", en: "Control units & complete headends" },
    { de: "Netzteile", en: "Power supplies" },
    { de: "Astro-Kopfstationen", en: "Astro headends" },
    { de: "DH8P, DH8Twin sowie DH2-, DH4- & DH8-Basis-Anlagen", en: "DH8P, DH8Twin as well as DH2, DH4 & DH8 base units" },
    { de: "Endverstärker & Sammelfelder", en: "Output amplifiers & antenna arrays" },
];

export default function RepairService() {
    const { t } = useLang();
    return (
        <section id="reparatur" data-testid="reparatur" className="scroll-mt-24 py-16 sm:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <Reveal>
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 text-white border border-slate-800 shadow-xl">
                        <div className="grid lg:grid-cols-2">
                            <div className="p-7 sm:p-10 lg:p-14 relative">
                                <Eyebrow light>{t("Express-Reparatur-Service", "Express repair service")}</Eyebrow>
                                <p className="mt-6 font-display text-6xl sm:text-7xl lg:text-8xl font-extrabold tracking-tighter leading-none">
                                    48<span className="text-red-400">h</span>
                                </p>
                                <h2 className="mt-4 font-display text-xl sm:text-2xl font-bold tracking-tight">
                                    {t("Kassetten-Reparatur zu Pauschalpreisen.", "Cassette repairs at fixed prices.")}
                                </h2>
                                <p className="mt-3 text-slate-400 text-sm sm:text-base leading-relaxed max-w-md">
                                    {t(
                                        "Pauschalreparaturen für Kopfstationstechnik von Grundig und baugleichen Anbietern — defekte Kassetten, Netz- und Bedienteile kommen bei uns schnell wieder aufs Netz.",
                                        "Fixed-price repairs for Grundig headend technology and identical brands — faulty cassettes, power and control units are quickly back on the network."
                                    )}
                                </p>
                                <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="repair-scope-list">
                                    {SCOPE.map((s) => (
                                        <li key={s.de} className="flex items-start gap-2.5 text-sm text-slate-300">
                                            <Check className="h-4 w-4 mt-0.5 shrink-0 text-emerald-400" />
                                            <span>{t(s.de, s.en)}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-9 flex flex-wrap gap-3">
                                    <a
                                        href="tel:+49919594040"
                                        data-testid="repair-phone-cta"
                                        className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 hover:bg-red-50 font-semibold text-sm px-6 py-3 transition-colors"
                                    >
                                        <Phone className="h-4 w-4" />
                                        {t("Reparaturauftrag per Telefon", "Place a repair order by phone")}
                                    </a>
                                    <a
                                        href="mailto:vertrieb@satmueller.de?subject=Reparaturauftrag%20Kopfstationstechnik"
                                        data-testid="repair-mail-cta"
                                        className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-white/60 font-semibold text-sm px-6 py-3 transition-colors"
                                    >
                                        <Mail className="h-4 w-4" />
                                        vertrieb@satmueller.de
                                    </a>
                                </div>
                            </div>
                            <div className="relative min-h-[280px] lg:min-h-full">
                                <img
                                    src={REPAIR_IMG}
                                    alt="Elektronik auf einer Platine"
                                    loading="lazy"
                                    data-testid="repair-image"
                                    className="absolute inset-0 h-full w-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/40 to-transparent" aria-hidden="true" />
                                <div className="absolute bottom-6 right-6 rounded-lg bg-white/10 backdrop-blur border border-white/20 px-4 py-3">
                                    <span className="eyebrow text-[10px] text-white">{t("Pauschalpreise · schnelle Abwicklung", "Fixed prices · fast processing")}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
